import React from 'react';
import { 
	Paper, 
	Typography, 
	Icon, 
	Button, 
	ButtonGroup, 
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Menu,
	MenuItem
} from '@material-ui/core';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import VncProgressDialog from '../dialogs/VncProgressDialog';

const useStyles = makeStyles(theme => ({
	root: {
		padding: theme.spacing(3, 2)
	},
	disabledButton: {
		backgroundColor: theme.palette.grey[300] + ' !important',
		color: theme.palette.text.secondary + ' !important'
	},
	vncButton: {
		minWidth: '180px', 
	},
}));

const AccessTab = ({ 
	cluster, 
	authenticatedUser, 
	marketplaceEntry,
	isLoading,
	launchVNCViewer,
	launchVNCWithOptions,
	terminateVNCSession,
	anchorEl,
	handleVncOptionsClick,
	handleVncOptionsClose,
	role,
	dispatch,
	Actions,
	authActions,
	uploadingSSHKey,
	uploadedSSHKeyName,
	fileInputRef,
	handleSSHKeyUpload,
	vncConnectionStatus,
	vncConnectionSteps,
	selectedAppInstructions,
	openInstructionDialog,
	setOpenInstructionDialog,
	handleOpenInstructions,
	closePopupBlockedNotification,
	isSafari,
	pendingClusterAppLaunch,
	setPendingClusterAppLaunch
}) => {
	const classes = useStyles();

	// Handler for the pending cluster app launch button
	const handlePendingLaunchClick = () => {
		if (pendingClusterAppLaunch && pendingClusterAppLaunch.clusterCommand) {
			// Launch VNC with cluster command
			// For Safari, force popup mode (false) to preserve user gesture context
			const forceTabMode = false; // Always use popup for Safari
			launchVNCViewer(forceTabMode, pendingClusterAppLaunch.clusterCommand);
			// Clear the pending launch
			setPendingClusterAppLaunch(null);
		}
	};

	const getServerIp = () => {
		return cluster?.info?.terraform?.publicIp || '';
	};

	return (
		<div className="flex flex-col w-full">
			{/* Pending Cluster App Launch Alert - Safari only */}
			{pendingClusterAppLaunch && (
				<Paper className="m-24 p-16 rounded-lg shadow-md bg-blue-50 border-2 border-blue-500">
					<div className="flex flex-col items-center">
						<div className="flex items-center mb-8">
							<Icon className="text-blue-700 text-32 mr-8">rocket_launch</Icon>
							<Typography className="text-20 font-bold text-blue-900">Ready to Launch Your Application</Typography>
						</div>
						<Typography className="text-14 text-gray-700 mb-16 text-center">
							Click the button below to open your cluster application. 
						</Typography>
						<Button
							variant="contained"
							color="primary"
							size="large"
							className="bg-blue-700 hover:bg-blue-800"
							startIcon={<Icon>play_arrow</Icon>}
							onClick={handlePendingLaunchClick}
							style={{
								minWidth: '300px',
								padding: '12px 24px',
								fontSize: '16px',
								fontWeight: 'bold'
							}}
						>
							Launch Application Now
						</Button>
					</div>
				</Paper>
			)}
			
			{/* Applications Section */}
			{marketplaceEntry && Array.isArray(marketplaceEntry.applications) && marketplaceEntry.applications.length > 0 && (
					<Paper className="m-24 p-16 rounded-lg shadow-md">
						<div className="flex items-center border-b-1 pb-8 mb-16">
							<Icon className="text-blue text-24 mr-8">apps</Icon>
							<Typography className="text-18 font-bold">Applications</Typography>
						</div>
						<div
							className="flex flex-row gap-8 overflow-x-auto pb-4"
							style={{ WebkitOverflowScrolling: 'touch' }}
						>
							{marketplaceEntry.applications.map((app, idx) => {
								const launchUrl = app.launch_link.replace('<server-ip>', getServerIp());
								return (
									<div
										key={app.id || app.name}
										className="flex flex-row items-center bg-white rounded-lg border-2 border-gray-200 transition-all duration-200 px-4 py-2 min-w-[320px] max-w-xs"
										style={{ minWidth: 280, maxWidth: 340 }}
									>
										{/* Icon with blue border on hover */}
										<div
											className="flex-shrink-0"
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											<Button
												className="transition-transform"
												style={{
													minWidth: 'auto',
													width: 56,
													height: 56,
													padding: 0,
													borderRadius: 12,
													background: '#f5f5f7',
													overflow: 'hidden',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													border: '2px solid #e5e7eb', // gray-200
													transition: 'border-color 0.2s',
												}}
												onMouseEnter={e => (e.currentTarget.style.borderColor = '#2563eb')}
												onMouseLeave={e => (e.currentTarget.style.borderColor = '#e5e7eb')}
												onClick={() => window.open(launchUrl, '_blank')}
											>
												<img
													src={`/assets/images/logos/${app.logo}`}
													alt={app.name}
													style={{ width: 36, height: 36, borderRadius: 6 }}
												/>
											</Button>
										</div>
										<div className="ml-12 flex-grow min-w-0">
											<Typography
												variant="subtitle1"
												className="font-medium text-blue-700 truncate cursor-pointer -mb-1"
												onClick={() => window.open(launchUrl, '_blank')}
											>
												{app.name}
											</Typography>
											<Typography
												variant="body2"
												color="textSecondary"
												className="line-clamp-2 mt-0"
												style={{ fontSize: '1.0rem', maxWidth: 180 }}
											>
												{app.description || "No description available"}
											</Typography>
											{app.instructions && (
												<Button
													size="small"
													color="primary"
													style={{ padding: '0px 4px', fontSize: '0.8rem', minHeight: 0, minWidth: 0, textTransform: 'none' }}
													className='-ml-1 -mt-1 bg-gray-200 hover:bg-gray-300'
													onClick={() => handleOpenInstructions(app.name, app.instructions)}
												>
													Instructions
												</Button>
											)}
										</div>
									</div>
								);
							})}
						</div>
						{/* Instructions Dialog */}
						<Dialog 
							open={openInstructionDialog} 
							onClose={() => setOpenInstructionDialog(false)}
							maxWidth="sm"
							fullWidth
						>
							<DialogTitle>
								{selectedAppInstructions.name} Instructions
							</DialogTitle>
							<DialogContent>
								<div
									style={{ fontSize: '1.5rem', lineHeight: 1.6 }}
									ref={el => {
										if (el) {
											// Fix links after rendering
											const links = el.querySelectorAll('a');
											links.forEach(link => {
												link.setAttribute('target', '_blank');
												link.setAttribute('rel', 'noopener noreferrer');
												// Fix href if it starts with a quote or is not absolute
												const href = link.getAttribute('href');
												if (href && /^["']?https?:\/\//.test(href)) {
													link.setAttribute('href', href.replace(/^["']|["']$/g, ''));
												}
											});
										}
									}}
									dangerouslySetInnerHTML={{
										__html: selectedAppInstructions.instructions
											? selectedAppInstructions.instructions.replace(/\\"/g, '"').replace(/\\'/g, "'")
											: ''
									}}
								/>
							</DialogContent>
							<DialogActions>
								<Button onClick={() => setOpenInstructionDialog(false)} color="primary">
									Close
								</Button>
							</DialogActions>
						</Dialog>
					</Paper>
				)}

				{/* Connection Information Section */}
				<div className="flex flex-col md:flex-row w-90 p-24">
					{/* Connection Information Card */}
					<Paper className="flex flex-col w-full md:w-1/2 mb-24 md:mb-0 md:mr-12 p-16 rounded-lg shadow-md">
						<div className="flex items-center border-b-1 pb-8 mb-16">
							<Icon className="text-blue text-24 mr-8">language</Icon>
							<Typography className="text-18 font-bold">Connection Information</Typography>
						</div>
						
						<div className="flex flex-col space-y-16">
							<div className="flex flex-col">
								<Typography className="text-14 font-medium text-gray-600 mb-4">Public IP Address</Typography>
								<div className="flex items-center bg-gray-100 rounded-md p-8">
									<Typography className="font-mono text-16 flex-grow">{cluster.info.terraform.publicIp}</Typography>
									<CopyToClipboard text={cluster.info.terraform.publicIp}>
										<IconButton size="small" className="text-blue-700 hover:bg-blue-50">
											<FileCopyOutlinedIcon />
										</IconButton>
									</CopyToClipboard>
								</div>
							</div>
							
							<div className="flex flex-col">
								<Typography className="text-14 font-medium text-gray-600 mb-4">Username</Typography>
								<div className="flex items-center bg-gray-100 rounded-md p-8">
									<Typography className="font-mono text-16 flex-grow">
										{authenticatedUser.username}
									</Typography>
									<CopyToClipboard text={authenticatedUser.username}>
										<IconButton size="small" className="text-blue-700 hover:bg-blue-50">
											<FileCopyOutlinedIcon />
										</IconButton>
									</CopyToClipboard>
								</div>
							</div>

							{authenticatedUser.participant && (
								<div className="flex flex-col">
									<Typography className="text-14 font-medium text-gray-600 mb-4">Password</Typography>
									<div className="flex items-center bg-gray-100 rounded-md p-8">
										<Typography className="font-mono text-16 flex-grow">{authenticatedUser.password}</Typography>
										<CopyToClipboard text={authenticatedUser.password}>
											<IconButton size="small" className="text-blue-700 hover:bg-blue-50">
												<FileCopyOutlinedIcon />
											</IconButton>
										</CopyToClipboard>
									</div>
								</div>
							)}
						</div>
					</Paper>

					{/* Access Methods Card */}
					<Paper className="flex flex-col w-full md:w-1/2 md:ml-12 p-16 rounded-lg shadow-md">
						<div className="flex items-center border-b-1 pb-8 mb-16">
							<Icon className="text-blue text-24 mr-8">settings_ethernet</Icon>
							<Typography className="text-18 font-bold">Access Methods</Typography>
						</div>
						
						<div className="grid grid-cols-2 gap-16">
							{/* Left Column */}
							<div className="flex flex-col space-y-16">
								{/* VNC Access Section */}
								{(/^ubuntu/.test(cluster.info['os-type']) ||
									['rocky-8', 'oel-8', 'oel-8-gpu', 'rocky-9'].includes(cluster.info['os-type'])) && (
									<div className="flex flex-col">
										<Typography className="text-14 font-medium text-gray-600 mb-8">Graphical Access (VNC)</Typography>
										
										<div className="flex flex-wrap gap-8">
											{isSafari() ? (
												// For Safari, render button group with dropdown but different menu options
												<ButtonGroup variant="contained" className={isLoading ? "mb-6" : "bg-blue-700 mb-6"}>
													<Button
														id="cluster_view_novnc"
														variant="contained"
														color="primary"
														className={clsx(
															isLoading ? classes.disabledButton : "bg-blue-700 hover:bg-blue-800",
															classes.vncButton
														)}
														startIcon={
															isLoading ? (
																<Icon className={classes.pulse}>hourglass_empty</Icon>
															) : (
																<Icon>desktop_windows</Icon>
															)
														}
														onClick={launchVNCViewer}
														disabled={isLoading}
														style={{
															minWidth: '200px',
															transition: 'all 0.3s ease'
														}}
													>
														{isLoading ? 'Connecting...' : 'Remote Desktop'}
													</Button>
													<Button
														color="primary"
														size="small"
														className={clsx(
															isLoading ? classes.disabledButton : "bg-blue-700 hover:bg-blue-800"
														)}
														aria-controls={Boolean(anchorEl) ? 'vnc-options-menu' : undefined}
														aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
														aria-haspopup="menu"
														onClick={handleVncOptionsClick}
														disabled={isLoading}
													>
														<ArrowDropDownIcon />
													</Button>
												</ButtonGroup>
											) : (
												// For other browsers, render button group with dropdown
												<ButtonGroup variant="contained" className={isLoading ? "mb-6" : "bg-blue-700 mb-6"}>
													<Button
														id="cluster_view_novnc"
														variant="contained"
														color="primary"
														className={clsx(
															isLoading ? classes.disabledButton : "bg-blue-700 hover:bg-blue-800",
															classes.vncButton
														)}
														startIcon={
															isLoading ? (
																<Icon className={classes.pulse}>hourglass_empty</Icon>
															) : (
																<Icon>desktop_windows</Icon>
															)
														}
														onClick={launchVNCViewer}
														disabled={isLoading}
														style={{
															minWidth: '200px',
															transition: 'all 0.3s ease'
														}}
													>
														{isLoading ? 'Connecting...' : 'Remote Desktop'}
													</Button>
													<Button
														color="primary"
														size="small"
														className={clsx(
															isLoading ? classes.disabledButton : "bg-blue-700 hover:bg-blue-800"
														)}
														aria-controls={Boolean(anchorEl) ? 'vnc-options-menu' : undefined}
														aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
														aria-haspopup="menu"
														onClick={handleVncOptionsClick}
														disabled={isLoading}
													>
														<ArrowDropDownIcon />
													</Button>
												</ButtonGroup>
											)}
										</div>
										
										{/* Popup blocked notification placed below button */}
										{vncConnectionStatus.error && vncConnectionStatus.error.type === 'popup_blocked' && (
											<div className="mt-12 p-12 rounded-lg" style={{ backgroundColor: '#fef3c7', border: '1px solid #f59e0b' }}>
												<div className="flex items-center">
													<Icon className="mr-8" style={{ color: '#b45309' }}>warning</Icon>
													<div className="flex-grow">
														<Typography className="text-14 font-medium" style={{ color: '#b45309' }}>
															{vncConnectionStatus.error.message}
														</Typography>
														<Typography className="text-12 mt-4" style={{ color: '#92400e' }}>
															{vncConnectionStatus.error.action}
														</Typography>
													</div>
													<div className="flex gap-8">
														{/* Only show "Open Tab" button for non-Safari browsers */}
														{!isSafari() && (
															<Button
																size="small"
																variant="contained"
																color="primary"
																onClick={() => launchVNCWithOptions(false, false)}
																className="ml-8"
																style={{ 
																	fontSize: '1.2rem',
																	fontWeight: 'bold',
																	padding: '6px 12px',
																	minWidth: 'auto',
																	backgroundColor: '#fde047',
																	color: '#374151',
																	boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
																}}
															>
																Open Tab
															</Button>
														)}
														<IconButton
															size="small"
															onClick={closePopupBlockedNotification}
															className="ml-4"
														>
															<Icon style={{ color: '#b45309' }}>close</Icon>
														</IconButton>
													</div>
												</div>
											</div>
										)}
									</div>
								)}

								{/* SSH Access Section */}
								<div className="flex flex-col">
									<Typography className="text-14 font-medium text-gray-600 mb-8">SSH Access</Typography>
									<div className="flex flex-wrap gap-8">
										<Button
											id="cluster_view_ssh_key"
											variant="contained"
											color="primary"
											className="bg-blue-700 hover:bg-blue-800"
											startIcon={<Icon>cloud_download</Icon>}
											onClick={() => dispatch(authActions.downloadUserSSHKey(true))}
										>
											Download SSH Key
										</Button>
									</div>
								</div>
							</div>

							{/* Right Column */}
							<div className="flex flex-col space-y-16">
								{/* OpenVPN Section  */}
								{role.includes('admin') && (
									<div className="flex flex-col">
										<Typography className="text-14 font-medium text-gray-600 my-8">VPN Access</Typography>
										<div className="flex flex-wrap gap-8">
											<Button
												id="cluster_view_openvpn_toggle"
												variant="contained"
												color={cluster.info.ovpn ? "default" : "primary"}
												startIcon={<Icon>{cluster.info.ovpn ? 'remove_circle' : 'add_circle'}</Icon>}
												className={cluster.info.ovpn ? "bg-gray-300 hover:bg-gray-400 mr-10" : "bg-blue-700 hover:bg-blue-800"}
												onClick={() =>
													dispatch(
														Actions.toggleOpenvpn({
															cluster: cluster.id,
															openvpn: cluster.info.ovpn
														})
													)
												}
											>
												{!cluster.info.ovpn ? 'Install OpenVPN' : 'Uninstall OpenVPN'}
											</Button>
											
											{cluster.info.ovpn && (
												<Button
													id="cluster_view_openvpn_download"
													variant="contained"
													color="primary"
													startIcon={<Icon>cloud_download</Icon>}
													className="bg-blue-700 hover:bg-blue-800"
													onClick={Actions.downloadOpenvpn(cluster)}
												>
													Download VPN Config
												</Button>
											)}
										</div>
									</div>
								)}
							</div>
						</div>
					</Paper>
				</div>

				{/* Connection Instructions Card */}
				<Paper className="m-24 p-16 mb-40 rounded-lg shadow-md">
					<div className="flex items-center border-b-1 pb-8 mb-16">
						<Icon className="text-blue text-24 mr-8">help_outline</Icon>
						<Typography className="text-18 font-bold">Connection Instructions</Typography>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-24">
						<div className="flex flex-col">
							<Typography className="text-16 font-medium mb-8">SSH Connection</Typography>
							<Typography className="text-14 mb-4">
								1. <span 
									className="text-blue-600 hover:underline cursor-pointer"
									onClick={() => dispatch(authActions.downloadUserSSHKey(true))}
								>
									Download the SSH key
								</span>
							</Typography>
							<Typography className="text-14 mb-4">2. Open your terminal</Typography>
							<Typography className="text-14 mb-4">3. Use the SSH key to connect with the following command:</Typography>
							<div className="bg-gray-100 p-8 rounded-md font-mono text-xs mb-8 flex items-center max-w-xl overflow-hidden">
								<span className="flex-grow pr-4 overflow-x-auto whitespace-normal break-all">{`chmod 600 ${authenticatedUser.username}-SSH-Key.pem && ssh -i ${authenticatedUser.username}-SSH-Key.pem`} {authenticatedUser.username}@{cluster.info.terraform.publicIp}</span>
								<CopyToClipboard text={`chmod 600 ${authenticatedUser.username}-SSH-Key.pem && ssh -i ${authenticatedUser.username}-SSH-Key.pem ${authenticatedUser.username}@${cluster.info.terraform.publicIp}`}>
									<IconButton size="small" className="text-blue-700 hover:bg-blue-50 flex-shrink-0">
										<FileCopyOutlinedIcon />
									</IconButton>
								</CopyToClipboard>
							</div>
							<Typography className="text-14 italic text-gray-600">
								Note: This command sets the correct permissions on your key file and connects in a single step
							</Typography>
						</div>

						{cluster.info.ovpn && (
							<div className="flex flex-col">
								<Typography className="text-16 font-medium mb-8">VPN Connection</Typography>
								<Typography className="text-14 mb-4">1. Download the OpenVPN configuration file</Typography>
								<Typography className="text-14 mb-4">2. Import the file into your OpenVPN client</Typography>
								<Typography className="text-14 mb-4">3. Connect to the VPN</Typography>
								<Typography className="text-14 italic text-gray-600 mt-4">
									For help installing an OpenVPN client, check out the{' '}
									<a href="https://openvpn.net/client-connect-vpn-for-windows/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
										official documentation
									</a>
								</Typography>
							</div>
						)}

						{(/^ubuntu/.test(cluster.info['os-type']) ||
							['rocky-8', 'oel-8', 'oel-8-gpu'].includes(cluster.info['os-type'])) && (
							<div className="flex flex-col mt-10">
								<Typography className="text-16 font-medium mb-8">VNC Connection</Typography>
								<Typography className="text-14 mb-4">1. Click the "Remote Desktop" button above</Typography>
								<Typography className="text-14 mb-4">2. A new browser tab or window will open</Typography>
								<Typography className="text-14 mb-4">3. If prompted, accept any security warnings</Typography>
								<Typography className="text-14 italic text-gray-600 mt-4">
									Note: VNC provides a graphical desktop interface to your cluster
								</Typography>
							</div>
						)}
					</div>
				</Paper>
			
				{/* SSH Key Upload Card */}
				<Paper className="m-24 p-16 mb-24 rounded-lg shadow-md mb-60">
					<div className="flex items-center border-b-1 pb-8 mb-16">
						<Icon className="text-blue text-24 mr-8">vpn_key</Icon>
						<Typography className="text-18 font-bold">Upload PEM File</Typography>
					</div>
					
					<div className="flex flex-col">
						<Typography className="text-14 mb-8">
							Upload your own SSH key (.pem file). The key will be placed in 
							<span className="font-mono bg-gray-100 px-2 mx-2">~/.ssh/</span>
							on the head node.
						</Typography>
						
						<div className="flex flex-col sm:flex-row items-center mt-8">
							<Button
								variant="contained"
								color={uploadingSSHKey ? "default" : "primary"}
								className={uploadingSSHKey 
									? "bg-gray-300 hover:bg-gray-300 mb-8 sm:mb-0 sm:mr-16" 
									: "bg-blue-700 hover:bg-blue-800 mb-8 sm:mb-0 sm:mr-16"}
								startIcon={uploadingSSHKey ? <Icon>hourglass_empty</Icon> : <CloudUploadIcon />}
								onClick={() => fileInputRef.current.click()}
								disabled={uploadingSSHKey}
							>
								{uploadingSSHKey ? "Uploading..." : "Select PEM File"}
							</Button>
							
							{uploadedSSHKeyName && (
								<div className="flex items-center px-3 py-2 rounded-md border border-green-200">
									<Typography className="text-gray-700 mr-2 font-medium">
										{uploadedSSHKeyName}
									</Typography>
									<Icon className="text-green-600">check_circle</Icon>
								</div>
							)}
							
							{!uploadedSSHKeyName && !uploadingSSHKey && (
								<Typography className="text-gray-600 italic">
									Only .pem extensions are supported
								</Typography>
							)}
							
							<input
								ref={fileInputRef}
								type="file"
								accept=".pem"
								style={{ display: 'none' }}
								onChange={handleSSHKeyUpload}
							/>
						</div>
						
						<Typography className="text-14 mt-16 text-gray-700">
							<strong>Note:</strong> After uploading, you can use the key for SSH connections between other systems 
							without needing to manually transfer it. This is useful for workflows involving secure copying (scp), 
							remote commands, or connecting to other systems.
						</Typography>
					</div>
				</Paper>
			
			{/* VNC Options Menu - Only for Safari browsers with popup-only options */}
			{isSafari() && (
				<Menu
					id="vnc-options-menu"
					anchorEl={anchorEl}
					open={Boolean(anchorEl)}
					onClose={handleVncOptionsClose}
					MenuListProps={{
						'aria-labelledby': 'vnc-options-button',
						style: {
							maxHeight: 'none'
						}
					}}
					PaperProps={{
						style: {
							zIndex: 9999,
							boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
							maxHeight: 'none'
						}
					}}
				>
					<MenuItem onClick={() => launchVNCWithOptions(false, true)}>
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							<span style={{ fontWeight: 'bold' }}>Open in Popup Window</span>
							<span style={{ fontSize: '0.8em', color: '#666' }}>Safari - Opens in new window</span>
						</div>
					</MenuItem>
					{cluster.info.vncRemoteClient && (
						<MenuItem onClick={() => launchVNCWithOptions(true, true)}>
							<div style={{ display: 'flex', flexDirection: 'column' }}>
								<span>Remote Client in Popup</span>
								<span style={{ fontSize: '0.8em', color: '#666' }}>Safari - Advanced option</span>
							</div>
						</MenuItem>
					)}
					<MenuItem onClick={terminateVNCSession}>
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							<span style={{ fontWeight: 'bold', color: '#d32f2f' }}>Terminate Remote Session</span>
							<span style={{ fontSize: '0.8em', color: '#d32f2f' }}>End any active VNC connections</span>
						</div>
					</MenuItem>
				</Menu>
			)}
			
			{/* VNC Progress Dialog */}
			<VncProgressDialog
				open={vncConnectionStatus.isConnecting}
				cluster={cluster}
				vncConnectionStatus={vncConnectionStatus}
				vncConnectionSteps={vncConnectionSteps}
			/>
		</div>
	);
};

// Custom comparison function for React.memo
const arePropsEqual = (prevProps, nextProps) => {
	// Only re-render if these specific props change
	const keysToCompare = [
		'cluster.id',
		'cluster.info.terraform.publicIp',
		'cluster.info.ovpn',
		'cluster.info.os-type',
		'cluster.info.vncRemoteClient',
		'authenticatedUser.username',
		'authenticatedUser.password',
		'authenticatedUser.participant',
		'marketplaceEntry',
		'isLoading',
		'anchorEl',
		'uploadingSSHKey',
		'uploadedSSHKeyName',
		'vncConnectionStatus',
		'openInstructionDialog',
		'selectedAppInstructions'
	];

	for (const key of keysToCompare) {
		const keys = key.split('.');
		let prevValue = prevProps;
		let nextValue = nextProps;
		
		for (const k of keys) {
			prevValue = prevValue?.[k];
			nextValue = nextValue?.[k];
		}
		
		if (prevValue !== nextValue) {
			return false;
		}
	}
	
	return true;
};

export default React.memo(AccessTab, arePropsEqual);
